import React, { useContext, useEffect, useRef, useState } from "react";
import { Avatar, Button, Collapse, Upload, theme } from "antd";
import { CalendarOutlined, CameraOutlined, InfoCircleOutlined, PictureOutlined, ShopOutlined, ShoppingOutlined } from "@ant-design/icons";
import ThemeContext from "../context/ThemeContext";
import { formatProductionEstimate } from "./productionUnitEstimate";
import "./production-unit-workspace.css";

export const useProductionWorkspaceStyle = () => {
  const { token } = theme.useToken();
  const appearance = useContext(ThemeContext);
  return {
    "--pu-surface": token.colorBgContainer,
    "--pu-text": token.colorText,
    "--pu-muted": token.colorTextSecondary,
    "--pu-heading": appearance?.isDarkMode ? token.colorTextHeading : token.colorPrimaryTextActive,
    "--pu-accent": appearance?.isDarkMode ? token.blue7 : token.colorPrimary,
    "--pu-on-accent": token.colorTextLightSolid,
    "--pu-button-bg": token.colorPrimary,
    "--pu-tint": token.blue1,
    "--pu-neutral": token.colorFillAlter,
    "--pu-line": token.colorBorderSecondary,
    "--pu-border": token.colorBorder,
    "--pu-warning": token.colorWarningText,
    "--pu-warning-bg": token.colorWarningBg,
    "--pu-radius": `${token.borderRadiusLG}px`,
  };
};

export const ProductionDisclosure = ({ sectionKey, title, description, expanded, onToggle, children }) => (
  <Collapse
    id={`production-section-${sectionKey}`}
    className="production-workspace__disclosure"
    activeKey={expanded ? [sectionKey] : []}
    onChange={(keys) => onToggle(sectionKey, keys.includes(sectionKey))}
    expandIconPosition="end"
    items={[{ key: sectionKey, forceRender: true,
      label: <span className="production-workspace__disclosure-label"><strong>{title}</strong><span>{description}</span></span>,
      children }]}
  />
);

export const useProductionImagePreview = (fileList) => {
  const file = Array.isArray(fileList) ? fileList[0] : undefined;
  const originalFile = file?.originFileObj;
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!(originalFile instanceof Blob)) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(originalFile);
    setPreview({ file: originalFile, url });
    return () => URL.revokeObjectURL(url);
  }, [originalFile]);

  return originalFile
    ? (preview?.file === originalFile ? preview.url : undefined)
    : file?.url || file?.thumbUrl;
};

export const ProductionUnitImageInput = ({ fileList = [], onChange, previewUrl, id }) => {
  const file = fileList[0];
  const existingImage = useRef(null);
  useEffect(() => {
    if (file && !file.originFileObj) existingImage.current = file;
  }, [file]);
  const fileInputId = `${id || "production-unit-image"}-file`;
  return (
    <div className={`production-workspace__photo${file ? " production-workspace__photo--selected" : ""}`}>
      {previewUrl ? (
        <img className="production-workspace__photo-preview" src={previewUrl} alt="Aperçu de l’unité" />
      ) : (
        <PictureOutlined className="production-workspace__photo-empty" aria-hidden="true" />
      )}
      <span className="production-workspace__photo-name" title={file?.name}>
        {file?.name || "Aucune image ajoutée"}
      </span>
      <label className="production-workspace__photo-file-label" htmlFor={fileInputId}>Fichier de l’unité</label>
      <Upload id={fileInputId} fileList={fileList} onChange={onChange} maxCount={1} accept="image/*"
        beforeUpload={() => false} showUploadList={false}>
        <Button id={id} icon={<CameraOutlined />} aria-label={file ? "Changer l’image" : "Ajouter une image"}>
          {file ? "Changer l’image" : "Ajouter une image"}
        </Button>
      </Upload>
      {file?.originFileObj && <Button type="link" onClick={() => onChange?.({ fileList: existingImage.current ? [existingImage.current] : [] })}>
        Annuler la sélection
      </Button>}
      <small>Image du produit · Facultatif</small>
    </div>
  );
};

const Amount = ({ value }) => <>{formatProductionEstimate(value)}{value !== null && value !== undefined ? " CFA" : ""}</>;

export const ProductionUnitSummary = ({
  name, imageUrl, unit, estimate, salesPrice, showEquipment, onEquipment,
  onReview, onCancel, saveAction, updatedAt,
  materialsTitle = "Matières premières", costContext = "production",
}) => (
  <aside className="production-workspace__summary" aria-label="Résumé de la configuration">
    <h2><ShopOutlined /> Résumé de la configuration</h2>
    <div className="production-workspace__summary-body">
      <div className="production-workspace__identity">
        <Avatar shape="square" size={52} src={imageUrl || undefined} icon={<ShoppingOutlined />} />
        <div><strong>{name || "Nouvelle configuration"}</strong><span>{unit}</span></div>
      </div>
      <div className="production-workspace__amount-row"><span>Quantité produite par défaut</span><strong>{formatProductionEstimate(estimate.quantity, 4)} {unit}</strong></div>
      <div className="production-workspace__unit-cost" aria-live="polite" aria-atomic="true">
        <span>Coût de revient unitaire estimé</span>
        <div><strong>{estimate.complete ? "≈ " : ""}<Amount value={estimate.unitCost} /></strong><span>par {unit}</span></div>
      </div>
      <div className="production-workspace__sale-price production-workspace__amount-row"><span>Prix de vente</span><strong><Amount value={salesPrice} /></strong></div>
      {!estimate.complete && <div className="production-workspace__estimate-note" role="status">
        <InfoCircleOutlined /><div>{estimate.reason}<Button type="link" onClick={onReview}>Vérifier la configuration</Button></div>
      </div>}
      <div className="production-workspace__breakdown">
        <h3>Pour {formatProductionEstimate(estimate.quantity, 4)} {unit}</h3>
        <dl>
          <div><dt>{materialsTitle}</dt><dd><Amount value={estimate.materialsCost} /></dd></div>
          <div><dt>Coûts variables</dt><dd><Amount value={estimate.variableCost} /></dd></div>
          {showEquipment && <div><dt>Amortissement</dt><dd>{estimate.hasEquipment ? <Amount value={estimate.depreciationCost} /> : "Non configuré"}</dd></div>}
        </dl>
      </div>
      <div className="production-workspace__total"><span>Coût estimé{showEquipment && !estimate.hasEquipment ? " hors amortissement" : " total"}</span><strong><Amount value={estimate.total} /></strong></div>
      {showEquipment && !estimate.hasEquipment && <div className="production-workspace__equipment-note">
        <InfoCircleOutlined /><div><strong>Aucun matériel associé <span>· Facultatif</span></strong>
          <p>L’estimation n’intègre pas d’amortissement.</p>
          <Button type="link" onClick={onEquipment}>Associer un matériel</Button>
        </div>
      </div>}
      <p className="production-workspace__estimate-source">Estimation selon les prix de référence du formulaire. Le coût réel est calculé lors de la {costContext}.</p>
      {updatedAt && !Number.isNaN(new Date(updatedAt).getTime()) && <p className="production-workspace__updated"><CalendarOutlined /> Modifié le {new Date(updatedAt).toLocaleString("fr-FR")}</p>}
      <div className="production-workspace__summary-actions"><Button onClick={onCancel}>Annuler</Button>{saveAction}</div>
    </div>
  </aside>
);
